import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import { SlPicture } from "react-icons/sl";
import { AiOutlineClose } from "react-icons/ai";
import { IoCloudUploadOutline } from "react-icons/io5";
import PaginationWrapper from "./PaginationWrapper";
import HoverButton from './HoverButton';
import { getBase64 } from '../utils/utils';

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out'
};

const activeStyle = {
    borderColor: '#2196f3'
};
const scroll = 3;

function Dropzone({ acceptedFileTypes, display, files, images, imagesObject, updateParent, isMobile, maxFiles, individual, text, submitted }) {
    const [dropzoneError, setDropzoneError] = useState('');
    const [displayFiles, setDisplayFiles] = useState([]);
    const [sortedDisplayFiles, setSortedDisplayFiles] = useState([]);

    const onDrop = useCallback((acceptedFiles, unacceptableFiles) => {
        if(unacceptableFiles && unacceptableFiles.length > 0) {
            setDropzoneError(unacceptableFiles[0].errors[0].message);
            return;
        }

        if(individual) {
            acceptedFiles.forEach((file) => {
                updateParent(file);
            });
        } else {
            updateParent(acceptedFiles);
        }
    }, [updateParent, individual]);

    const { getRootProps, getInputProps, isDragActive, /* acceptedFiles,*/ } = useDropzone({ accept: acceptedFileTypes, onDrop, maxFiles });

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
    }), [isDragActive]);

    const buildFilesForDisplay = async () => {
        let filesForDisplay = [];
        if(files) {
            //Update files array for graphical display here
            filesForDisplay = files.map((file, index) => {
                if(file && (file.path || file.name)) {
                    let display = file.path ? file.path : file.name;
                    let size = file.size ? file.size : null;
                    return (
                        <div
                            style={files.length > scroll ? { "width": "95%" } : {}}
                            className='dropzone-file'
                            key={`${display}${index}`}
                        >   
                            <div style={{ "marginRight": "32px", "display": "flex", "alignItems": "center", "padding": "12px", "border": "2px solid #e8eaf6", "borderRadius": "8px" }}>
                                <SlPicture style={{ "transform": "scale(1.5)" }} />
                            </div>
                            <div>
                                <p style={{ "margin": "0px" }}>{display}</p>
                                {size ? (
                                    <p style={{ "margin": "0px", "fontStyle": "italic" }}>{(size / 1000000).toFixed(2)} MB</p>
                                ) : null}
                            </div>
                            <div style={{ "width": "100%", "textAlign": "right", "paddingRight": "6px" }}>
                                <HoverButton 
                                    normalIcon={<AiOutlineClose style={{ "transform": "scale(1.5)", "color": "grey" }} className="ai-edit-icon"/>}
                                    hoverIcon={<AiOutlineClose style={{ "transform": "scale(1.5)", "color": "black" }} className="ai-edit-icon"/>}
                                    updateParent={updateParent} 
                                    updateObject={file}
                                    type={"delete"}
                                    style={{ "display": "inline" }}
                                />
                            </div>
                        </div>
                    );
                } else {
                    return false;
                }
            });
        } else if(imagesObject) {
            //Update files array for graphical display here
            filesForDisplay = await Promise.all(Object.keys(imagesObject).map(async(id, index) => {
                let imageObject = imagesObject[id];
                let base64Image = await getBase64(imageObject.file);
                return (
                    <div
                        className='dropzone-image-object'
                        key={JSON.stringify(imageObject)}
                    >   
                        <div style={{ "display": "flex" }}>
                            {imageObject.error ? (
                                <p style={isMobile ? { "width": "65vw", "marginRight": "27px", "height": "200px" } : { "width": "150px", "marginRight": "27px", "height": "104px" }} className='form-error'>Failed to upload image</p>
                            ) : (
                                <React.Fragment>
                                    <img style={isMobile ? { "width": "65vw" } : { "width": "150px" }} alt={`Dropzone File ${index + 1}`} src={base64Image} />
                                    {imageObject.success || submitted ? (
                                        <div style={{ "width": "16px" }}></div>
                                    ) : (
                                        <div>
                                            <HoverButton
                                                normalIcon={<AiOutlineClose style={{ "transform": "scale(1.5)", "color": "grey" }} className="ai-edit-icon"/>}
                                                hoverIcon={<AiOutlineClose style={{ "transform": "scale(1.5)", "color": "black" }} className="ai-edit-icon"/>}
                                                updateParent={updateParent} 
                                                updateObject={id}
                                                type={"delete"}
                                                style={isMobile ? { "alignItems": "top", "marginLeft": "3px", "marginRight": "4px", "width": "100%", "justifyContent": "right" } : { "alignItems": "top", "marginLeft": "3px", "marginRight": "4px" }}
                                            />
                                        </div>
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                        <div>
                            <p style={{ "margin": "0px" }}>{imageObject.file.name}</p>
                        </div>
                    </div>
                );
            }));
        } else if(images) {
            //Update files array for graphical display here
            filesForDisplay = images.map((file, index) => {
                if(file) {
                    return (
                        <div
                            className='dropzone-image'
                            key={file}
                        >   
                            <img style={isMobile ? { "width": "65vw" } : { "width": "150px" }} alt={`Dropzone File ${index + 1}`} src={file} />
                            <HoverButton
                                normalIcon={<AiOutlineClose style={{ "transform": "scale(1.5)", "color": "grey" }} className="ai-edit-icon"/>}
                                hoverIcon={<AiOutlineClose style={{ "transform": "scale(1.5)", "color": "black" }} className="ai-edit-icon"/>}
                                updateParent={updateParent} 
                                updateObject={file}
                                type={"delete"}
                                style={isMobile ? { "alignItems": "top", "marginLeft": "3px", "marginRight": "4px", "width": "100%", "justifyContent": "right" } : { "alignItems": "top", "marginLeft": "3px", "marginRight": "4px" }}
                            />
                        </div>
                    );
                } else {
                    return false;
                }
            });  
        }
        return filesForDisplay;
    };

    useEffect(() => {
        buildFilesForDisplay().then((filesForDisplay) => {
            setDisplayFiles(filesForDisplay);
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images, files, imagesObject, isMobile, submitted]);

    let internal_text = `Drag 'n' drop some files here, or click to select files`;
    if(text) {
        internal_text = text
    }

    return (
        <div style={{ "cursor": "pointer" }} className="container">
            <div {...getRootProps({style})}>
                <input {...getInputProps()} />
                <IoCloudUploadOutline style={{ "transform": "scale(1.5)" }} />
                <p style={{ "marginTop": "4px", "textAlign": "justify" }}>{internal_text}</p>
            </div>
            <div style={{ "textAlign":"center", "marginTop":"16px", "paddingBottom": "0px" }} className="form-error">{dropzoneError}</div>
            {display && files && files.length > 0 && displayFiles && displayFiles.length > 0 ? (
                <div style={files.length > scroll ? { "width": "100%", "whiteSpace":"nowrap", "overflowY": "scroll", "maxHeight": "325px" } : { "width": "100%", "whiteSpace":"nowrap", "maxHeight": "325px" }}>
                    {displayFiles}
                </div>
            ) : null}
            {display && ((images && images.length > 0) || (imagesObject && Object.keys(imagesObject).length > 0)) && displayFiles && displayFiles.length > 0 ? (
                <React.Fragment>
                    <div style={{ "display": "flex", "justifyContent": "center", "alignItems": "center" }}>
                        <PaginationWrapper inputData={displayFiles} setSortedData={setSortedDisplayFiles} pageLimit={100} pageNeighbours={1}/>
                    </div>
                    <div style={isMobile ? { "width": "100%" } : { "width": "100%", "display": "flex", "whiteSpace":"nowrap", "flexWrap": "wrap" }}>
                        {sortedDisplayFiles}
                    </div>
                </React.Fragment>
            ) : null}
        </div>
    );
}

Dropzone.propTypes = {
    acceptedFileTypes: PropTypes.object.isRequired,
    display: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool,
    updateParent: PropTypes.func,
    text: PropTypes.string,
    files: PropTypes.array,
    images: PropTypes.array,
    imagesObject: PropTypes.object,
    maxFiles: PropTypes.number.isRequired,
    submitted: PropTypes.bool,
    individual: PropTypes.bool
};

export default Dropzone;